/**
 * AIM Color Selectors — Swatch picker + line-item property bridge
 *
 * 1. Renders as native color-swatch elements (replicating option-value.liquid)
 * 2. Reads color data from theme customizer (data-color-map) with JS fallback
 * 3. Injects hidden properties[Key] inputs INTO the Shopify product form
 * 4. Hides the native variant picker (product has 1 "Default" variant)
 * 5. Validates all selectors are chosen before allowing add-to-cart
 */

(function () {
  var container = document.querySelector('[data-aim-color-selectors]');
  if (!container) return;

  var formId = container.getAttribute('data-form-id');
  var minQuantity = parseInt(container.getAttribute('data-min-quantity'), 10) || 1;
  var form = document.getElementById(formId);
  // Fallback: find form by product-form ID pattern
  if (!form) {
    form = document.querySelector('form[id*="product-form-"]');
  }
  if (!form) {
    console.warn('AIM Color Selectors: could not find product form');
    return;
  }

  // ── Color map — config from theme customizer, with JS fallback ──
  var configMap = {};
  var configRaw = container.getAttribute('data-color-map');
  if (configRaw) {
    try {
      configMap = JSON.parse(configRaw);
    } catch (e) {
      console.warn('AIM Color Selectors: could not parse data-color-map', e);
    }
  }

  // JS fallback for colors not in the theme config
  var fallbackMap = {
    'Apple Green': '#8DB600',
    'Black': '#0A0A0A',
    'Blue': '#1565C0',
    'Boysenberry': '#873260',
    'Claret': '#7F1734',
    'Green': '#2E7D32',
    'Grey': '#9E9E9E',
    'Harvest Gold': '#DA9100',
    'Hot Pink': '#FF69B4',
    'Ivory': '#FFFFF0',
    'Khaki': '#C3B091',
    'Kraft Brown': '#A0522D',
    'Lavender': '#B57EDC',
    'Limon': '#C7F000',
    'Mandarin Red': '#E64A19',
    'Midnight Blue': '#191970',
    'Orange': '#FF9800',
    'Pink': '#FFC0CB',
    'Purple': '#7B1FA2',
    'Scarlet Red': '#FF2400',
    'Teal': '#008080',
    'Turquoise': '#40E0D0',
    'White': '#FFFFFF',
    'Yellow': '#FFEB3B',

    'DayGlo Green': '#39FF14',
    'DayGlo Limon': '#CCFF00',
    'DayGlo Pink': '#FF6EC7',
    'DayGlo White': '#FFFFF0',

    'Glimmer Gold': '#FFD700',
    'Glimmer Silver': '#C0C0C0',

    'Metallic Black': '#1A1A1A',
    'Metallic Blue': '#1E90FF',
    'Metallic Clear': '#F0F0F0',
    'Metallic Copper-Bronze': '#CD7F32',
    'Metallic Gold': '#FFD700',
    'Metallic Green': '#32CD32',
    'Metallic Holographic': '#E8E8E8',
    'Metallic Mearl': '#F5F5DC',
    'Metallic Orange': '#FF8C00',
    'Metallic Pink': '#FF69B4',
    'Metallic Purple': '#9B30FF',
    'Metallic Red': '#DC143C',
    'Metallic Silver': '#C0C0C0',
    'Metallic Teal': '#20B2AA',
    'Metallic White': '#F8F8FF',

    'DayGlo Film Blue': '#4169E1',
    'DayGlo Film Green': '#32CD32',
    'DayGlo Film Orange': '#FF8C00',
    'DayGlo Film Pink': '#FF69B4',
    'DayGlo Film Purple': '#9B30FF',
    'DayGlo Film Yellow': '#FFD700',

    'Technoflek Black': '#1A1A1A',
    'Technoflek Blue': '#4169E1',
    'Technoflek Copper': '#B87333',
    'Technoflek Gold': '#FFD700',
    'Technoflek Green': '#32CD32',
    'Technoflek Mearl': '#F5F5DC',
    'Technoflek Orange': '#FF8C00',
    'Technoflek Pink': '#FF69B4',
    'Technoflek Purple': '#9B30FF',
    'Technoflek Red': '#DC143C',
    'Technoflek Silver': '#C0C0C0',
    'Technoflek White': '#F8F8FF',

    'CleanFree White': '#FFFFFF',
    'Holographic': '#E8E8E8'
  };

  /**
   * Merge configMap over fallbackMap. Config wins for any key present in both.
   * For ring-inset detection we need the hex value, so we extract it from the CSS value.
   */
  function getHex(colorName) {
    var css = configMap[colorName];
    if (css) {
      // Extract hex from CSS value like "linear-gradient(to right, #FF0000, #FF0000)"
      var match = css.match(/#([0-9A-Fa-f]{6})/);
      if (match) return match[0];
    }
    return fallbackMap[colorName] || null;
  }

  function isLightColor(colorName) {
    var hex = getHex(colorName);
    if (!hex) return false;
    var r = parseInt(hex.slice(1, 3), 16);
    var g = parseInt(hex.slice(3, 5), 16);
    var b = parseInt(hex.slice(5, 7), 16);
    return r >= 220 && g >= 220 && b >= 220;
  }

  // ── Apply swatch styles + ring-inset ─────────────────────────
  var allSwatches = container.querySelectorAll('[data-aim-option]');
  allSwatches.forEach(function (label) {
    var colorName = label.getAttribute('data-aim-value');

    // If Liquid didn't set an inline style, apply from config or fallback
    if (!label.getAttribute('style')) {
      var cssValue = configMap[colorName];
      if (!cssValue) {
        var hex = fallbackMap[colorName];
        if (hex) {
          cssValue = 'linear-gradient(to right, ' + hex + ', ' + hex + ')';
        }
      }
      if (cssValue) {
        label.style.setProperty('--swatch-background', cssValue);
      }
    }

    // Apply ring-inset for light colors
    if (isLightColor(colorName)) {
      label.classList.add('ring-inset');
      label.style.outline = '1px solid #ddd';
    }
  });

  // ── Selection handling ───────────────────────────────────────
  var selectorGroups = container.querySelectorAll('[data-aim-selector]');

  /**
   * Toggle .is-selected on the parent .color-swatch label.
   * This triggers the native theme's ring pseudo-element:
   *   .color-swatch.is-selected:before { opacity: 1; transform: scale(1); }
   */
  function markSelected(group) {
    var labels = group.querySelectorAll('[data-aim-option]');
    var checkedRadio = group.querySelector('[data-aim-input]:checked');
    labels.forEach(function (l) { l.classList.remove('is-selected'); });
    if (checkedRadio && checkedRadio.parentElement) {
      checkedRadio.parentElement.classList.add('is-selected');
    }
  }

  selectorGroups.forEach(function (group) {
    var idx = group.getAttribute('data-aim-selector');
    var inputs = group.querySelectorAll('[data-aim-input]');

    // Initial state: ring on any pre-checked radio
    markSelected(group);

    inputs.forEach(function (radio) {
      radio.addEventListener('change', function () {
        markSelected(group);
        updatePropertyInputs();
        updateSelectedLabel(idx, radio.value);
      });
    });
  });

  // ── Update selected label text ───────────────────────────────
  function updateSelectedLabel(idx, value) {
    var label = container.querySelector('[data-aim-selected-label="' + idx + '"]');
    if (label) {
      label.textContent = value;
    }
  }

  // ── Inject/update hidden properties in the form ──────────────
  function updatePropertyInputs() {
    // Remove all existing property inputs
    form.querySelectorAll('[data-aim-property]').forEach(function (el) { el.remove(); });

    selectorGroups.forEach(function (group) {
      var idx = group.getAttribute('data-aim-selector');
      var checked = group.querySelector('[data-aim-input]:checked');
      if (checked) {
        var legend = group.querySelector('[data-aim-legend]');
        var key = legend ? legend.textContent.trim() : ('Color ' + idx);
        // Strip trailing colon (e.g. "Tissue Color 1:" → "Tissue Color 1")
        key = key.replace(/:+$/, '').trim();

        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[' + key + ']';
        input.value = checked.value;
        input.setAttribute('data-aim-property', '');
        form.appendChild(input);
      }
    });
  }

  // Initial property injection (nothing selected yet)
  updatePropertyInputs();

  // ── Minimum quantity setup ──────────────────────────────────
  if (minQuantity > 1) {
    // Default quantity to minimum
    var qtyInput = document.getElementById(formId + '-quantity');
    if (qtyInput) {
      qtyInput.value = minQuantity;
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  } else {
    // Hide notice when no minimum
    var notice = container.querySelector('.aim-color-selectors__min-notice');
    if (notice) notice.style.display = 'none';
  }

  // ── Hide native variant picker ───────────────────────────────
  function hideVariantPicker() {
    var pickers = document.querySelectorAll('variant-selects, variant-radios');
    pickers.forEach(function (el) { el.style.display = 'none'; });

    var inputs = document.querySelectorAll('.product-form__input');
    inputs.forEach(function (el) {
      var label = el.querySelector('.form__label');
      if (label) {
        var text = label.textContent.trim();
        if (text === 'Title' || text === 'Default') {
          el.style.display = 'none';
        }
      }
    });
  }
  hideVariantPicker();
  document.addEventListener('shopify:section:load', hideVariantPicker);

  // ── Validation: prevent buy-buttons click if incomplete ──────
  document.addEventListener('click', function (e) {
    // Only intercept clicks on the buy-buttons for OUR form
    var submitBtn = e.target.closest('buy-buttons [type="submit"], buy-buttons button[type="submit"]');
    if (!submitBtn) return;
    var buyButtons = submitBtn.closest('buy-buttons');
    if (!buyButtons) return;
    var bbFormId = buyButtons.getAttribute('form');
    if (bbFormId !== formId) return;

    // Validate all selectors are filled
    var allFilled = true;
    selectorGroups.forEach(function (group) {
      var idx = group.getAttribute('data-aim-selector');
      var checked = group.querySelector('[data-aim-input]:checked');
      if (!checked) {
        allFilled = false;
        group.classList.add('color-selector--error');
        setTimeout(function () {
          group.classList.remove('color-selector--error');
        }, 500);
      }
    });

    if (!allFilled) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }

    // Validate minimum quantity
    if (minQuantity > 1) {
      var qtyInput = document.getElementById(formId + '-quantity');
      var qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
      if (qty < minQuantity) {
        e.preventDefault();
        e.stopImmediatePropagation();
        container.classList.add('color-selector--qty-error');
        // Flash the quantity input
        if (qtyInput) {
          qtyInput.classList.add('aim-qty-error');
          setTimeout(function () {
            qtyInput.classList.remove('aim-qty-error');
            container.classList.remove('color-selector--qty-error');
          }, 2000);
        }
        return;
      }
    }
    // If all filled, updatePropertyInputs() already ran via change events
  }, true); // CAPTURE phase — fires before buy-buttons' own click handler
})();
